import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import packageActions from "../../../../Actions/Voss/packageActions";
import subscriptionActions from "../../../../Actions/Voss/subscriptionActions";
import customerActions from "../../../../Actions/customerActions";
import Card, { CardContainer } from "../../../../Components/Card/Card";
import TabsCard from "../../../../Components/Card/TabsCard";
import { Licenses } from "../../../../Components/Customer/VOSS/Subscriptions/Licenses";
import { Button, ButtonOption } from "../../../../Components/Fundamentals/Button";
import ConfirmDeleteModal from "../../../../Components/Modals/ConfirmDeleteModal/ConfirmDeleteModal";
import ConfirmSubscriptionChangeModal from "../../../../Components/Modals/ConfirmSubscriptionChange/ConfirmSubscriptionChangeModal";
import SubscriptionTerminationModal from "../../../../Components/Modals/SubscriptionTermination/SubscriptionTerminationModal";
import customerRoutes from "../../../../Constants/Customer/customerRoutes";
import { vossPaymentStatus } from "../../../../Constants/VOSS/vossPaymentStatus";
import { VossSubscriptionItemStatus } from "../../../../Constants/VOSS/vossSubscriptionItemStatus";
import forms from "../../../../Constants/forms";
import Modals from "../../../../Constants/modals";
import useModal from "../../../../Hooks/useModal";
import helpers from "../../../../Utils/helpers";
import subscriptionUtils from "../../../../Utils/subscriptionUtils";
import targetGroupHelpers from "../../../../Utils/targetGroups";
import EditSubscriptionContainer from "./EditSubscriptionContainer";
import { ProductGroups } from "./ProductGroups";
import { ScheduledChanges } from "./ScheduledChanges";
import { SubscriptionMainInfo } from "./SubscriptionMainInfo";
import {VossIntermediaryStatus} from "../../../../Constants/VOSS/vossIntermediaryStatus";

const CustomerSubscriptionContainer = () => {
  const { subscriptionId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isModalOpen, closeModal, openModal } = useModal();

  const customer = useSelector((state) => state.CustomerReducer.customer);
  const subscription = useSelector((state) => state.SubscriptionReducer.subscription);
  const subscriptions = useSelector((state) => state.CustomerReducer.subscriptions);
  const subscriptionProductGroups = useSelector((state) => state.SubscriptionReducer.subscriptionProductGroups);
  const loading = useSelector((state) =>
    state.CommonReducer.dataLoading.subscription ||
    state.CommonReducer.dataLoading.productGroups ||
    state.CommonReducer.dataLoading.offer ||
    state.CommonReducer.dataLoading.collaborations
  );
  const intermediaries = useSelector((state) => state.CommonReducer.intermediaries);
  const offer = useSelector((state) => state.PackageReducer.offer);
  const vossInvoices = useSelector((state) => state.CustomerReducer.vossCustomerInvoices);
  const collaborations = useSelector((state) => state.CustomerReducer.collaborations);
  const subscriptionForm = useSelector((state) => state.CommonReducer.forms && state.CommonReducer.forms[forms.modifySubscription]);
  const subscriptionLicenses = useSelector((state) => state.SubscriptionReducer.subscriptionLicenses);
  const blockEdit = !!subscriptionUtils.tierIsNonInteractive(subscription?.tier);

  useEffect(() => {
    if (subscription) {
      dispatch(subscriptionActions.initializeModifySubscriptionForm(subscription));
    }
  }, [subscription]);


  useEffect(() => {
    dispatch(subscriptionActions.getSubscription(subscriptionId));

    let controller = new AbortController();

    if (!intermediaries)
      dispatch(subscriptionActions.getIntermediaries(VossIntermediaryStatus.Active));

    if (!subscriptions)
      dispatch(customerActions.searchSubscriptions(customer.vossCustomer.id));

    if (!vossInvoices)
      dispatch(customerActions.getVossCustomerInvoices(customer.vossCustomer.id, vossPaymentStatus.Unpaid));

    if (!collaborations)
      dispatch(customerActions.getCollaborations(customer.vonCustomer.id, false, controller));

    return () => {
      dispatch(subscriptionActions.clear("subscription"));
      dispatch(subscriptionActions.clear("subscriptionProductGroups"));
      dispatch(subscriptionActions.clear("subscriptionLicenses"));
      dispatch(subscriptionActions.clear("availableMigration"));
      dispatch(packageActions.clear("offer"));
      controller.abort();
    }
  }, [])

  useEffect(() => {
    if (subscription) {
      if (!offer) {
        dispatch(packageActions.getOffer(subscription.package.id, subscription.customer.id));
      }
      if (!subscriptionProductGroups) {
        dispatch(subscriptionActions.getSubscriptionProductGroups(subscription.id, VossSubscriptionItemStatus.Active));
      }
      if (!subscriptionLicenses)
        dispatch(subscriptionActions.getSubscriptionLicenses(customer.vossCustomer.id, subscriptionId, VossSubscriptionItemStatus.Active));

      if (customer?.vossCustomer?.id && subscription.package?.id) {
        dispatch(subscriptionActions.getAvailableMigration(customer.vossCustomer.id, subscription.package.id));
      }
    }

  }, [subscription])

  const isDeleting = useSelector((state) => state.CommonReducer.dataLoading.deleteSubscriptionTermination);

  const reloadData = () => {
    dispatch(customerActions.searchSubscriptions(customer.vossCustomer.id));
    dispatch(subscriptionActions.getSubscription(subscription.id))
  }

  const onDeleteSubscriptionTermination = () => {
    dispatch(subscriptionActions.deleteSubscriptionTermination(subscription.id, () => {
      reloadData();
      closeModal(Modals.ConfirmDeleteModal);
    }));
  }

  const onSave = () => {
    openModal(Modals.ConfirmSubscriptionChange);
  }

  return (<CardContainer>
    <SubscriptionMainInfo isLoading={loading} blockEdit={blockEdit} />
    <ScheduledChanges blockEdit={blockEdit} reloadData={reloadData} />
    <TabsCard isLoading={loading}>
      <TabsCard.Tab label={t("subscriptions.subscription")}>
        <TabsCard.TabBody isLoading={loading}>
          <EditSubscriptionContainer blockEdit={blockEdit} />
        </TabsCard.TabBody>
        <TabsCard.TabFooter>
          <Card.Footer.Buttons>
            <Button onClick={() => navigate(customerRoutes.customer(customer.pathId))}>{t('common.cancel')}</Button>
            <Button option={ButtonOption.PRIMARY} disabled={blockEdit || !subscriptionForm?.changed} onClick={() => onSave()}>{t('common.save')}</Button>
          </Card.Footer.Buttons>
        </TabsCard.TabFooter>
      </TabsCard.Tab>
      <TabsCard.Tab label={t("subscriptions.products")}>
        <TabsCard.TabBody>
          <ProductGroups productGroups={subscriptionProductGroups} customer={customer} subscription={subscription} />
        </TabsCard.TabBody>
      </TabsCard.Tab>
      <TabsCard.Tab label={t("subscriptions.licenses")}>
        <TabsCard.TabBody>
          <Licenses subscription={subscription} subscriptionLicenses={subscriptionLicenses} blockEdit={blockEdit} />
        </TabsCard.TabBody>
      </TabsCard.Tab>
    </TabsCard>
    {isModalOpen(Modals.SubscriptionTermination) && <SubscriptionTerminationModal subscription={subscription} />}
    {isModalOpen(Modals.ConfirmDeleteModal) && <ConfirmDeleteModal isLoading={isDeleting} onSubmit={() => onDeleteSubscriptionTermination(subscription.id)} title={t('editsubscriptioncontainer.cancelTermination')} itemName={t('editsubscriptioncontainer.confirmCancelTermination', { value1: subscription.package.name, value2: helpers.formatDate(subscription.termination.terminationDate) })} />}
    {isModalOpen(Modals.ConfirmSubscriptionChange) && <ConfirmSubscriptionChangeModal subscription={subscription} form={subscriptionForm} offer={offer} reloadData={reloadData} />}
  </CardContainer>
  );
};

export default CustomerSubscriptionContainer;